import { QueryKeys } from '@gokrew/models/constants';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { mergeQueryOptions } from '@/utility/queryOptions';
import {
  getDashboardSummary,
  getEmployeesOnLeave,
  getAssetTypeDistribution,
  getCompanyTasksSummary,
  getDashboardAttendance,
  getDashboardCards,
  getUpcomingChanges,
  getDashboardBirthdays,
} from '@/api/dashboard';
import type {
  GetDashboardSummaryResponse,
  GetEmployeesOnLeaveResponse,
  GetAssetTypeDistributionResponse,
  CompanyTasksSummary,
  GetDashboardAttendanceResponse,
  GetDashboardCardsResponse,
} from '@/types/dashboard';

const DashboardQueryKeys = {
  summary: 'summary',
  employeesOnLeave: 'employees-on-leave',
  assetTypeDistribution: 'asset-type-distribution',
  companyTasksSummary: 'company-tasks-summary',
  attendance: 'attendance',
  cards: 'cards',
  upcomingChanges: 'upcoming-changes',
  birthdays: 'birthdays',
} as const;

export function useGetDashboardSummary(
  options: Omit<
    UseQueryOptions<GetDashboardSummaryResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId, branchId: authBranchId } = useAuth();

  return useQuery<GetDashboardSummaryResponse>({
    queryKey: [QueryKeys.Dashboard, DashboardQueryKeys.summary, authBranchId],
    queryFn: () => getDashboardSummary(companyId!, authBranchId ?? undefined),
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export function useGetEmployeesOnLeave(
  options: Omit<
    UseQueryOptions<GetEmployeesOnLeaveResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId, branchId: authBranchId } = useAuth();

  return useQuery<GetEmployeesOnLeaveResponse>({
    queryKey: [
      QueryKeys.Dashboard,
      DashboardQueryKeys.employeesOnLeave,
      authBranchId,
    ],
    queryFn: () => getEmployeesOnLeave(companyId!, authBranchId ?? undefined),
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export function useGetAssetTypeDistribution(
  options: Omit<
    UseQueryOptions<GetAssetTypeDistributionResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId, branchId: authBranchId } = useAuth();

  return useQuery<GetAssetTypeDistributionResponse>({
    queryKey: [
      QueryKeys.Dashboard,
      DashboardQueryKeys.assetTypeDistribution,
      authBranchId,
    ],
    queryFn: () =>
      getAssetTypeDistribution(companyId!, authBranchId ?? undefined),
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export function useGetCompanyTasksSummary(
  options: Omit<
    UseQueryOptions<CompanyTasksSummary>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId } = useAuth();

  return useQuery<CompanyTasksSummary>({
    queryKey: [QueryKeys.Dashboard, DashboardQueryKeys.companyTasksSummary],
    queryFn: async () => {
      const res = await getCompanyTasksSummary(companyId!);
      return res?.data as CompanyTasksSummary;
    },
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export function useGetDashboardAttendance(
  options: Omit<
    UseQueryOptions<GetDashboardAttendanceResponse>,
    'queryKey' | 'queryFn'
  > = {},
  /** Overrides the header's location (undefined keeps it, null = all). */
  branchOverride?: string | null
) {
  const { companyId, branchId: authBranchId } = useAuth();
  const branchId = branchOverride === undefined ? authBranchId : branchOverride;

  return useQuery<GetDashboardAttendanceResponse>({
    queryKey: [
      QueryKeys.Dashboard,
      DashboardQueryKeys.attendance,
      companyId,
      branchId,
    ],
    queryFn: () => getDashboardAttendance(companyId!, branchId ?? undefined),
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export function useGetDashboardCards(
  options: Omit<
    UseQueryOptions<GetDashboardCardsResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId, branchId: authBranchId } = useAuth();

  return useQuery<GetDashboardCardsResponse>({
    queryKey: [QueryKeys.Dashboard, DashboardQueryKeys.cards, authBranchId],
    queryFn: () => getDashboardCards(companyId!, authBranchId ?? undefined),
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

/** The Upcoming changes drawer's events; fetched only while it is open. */
export function useUpcomingChanges(enabled: boolean) {
  const { companyId, branchId: authBranchId } = useAuth();
  return useQuery({
    queryKey: [
      QueryKeys.Dashboard,
      DashboardQueryKeys.upcomingChanges,
      authBranchId,
    ],
    queryFn: async () =>
      (await getUpcomingChanges(companyId!, authBranchId ?? undefined)).data,
    enabled: enabled && !!companyId,
  });
}

/** The Birthdays card: today, tomorrow and the next seven days. */
export function useDashboardBirthdays() {
  const { companyId, branchId: authBranchId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.Dashboard, DashboardQueryKeys.birthdays, authBranchId],
    queryFn: async () =>
      (await getDashboardBirthdays(companyId!, authBranchId ?? undefined)).data,
    enabled: !!companyId,
    // Only changes at company midnight or when a date of birth is edited.
    staleTime: 5 * 60 * 1000,
  });
}
